import {
  type ChainFullId,
  type ChainId,
  ChainIds,
  type MultichainTokenBalance,
} from "@umami/chains";
import { type UnifiedBalanceWithPrices } from "@umami/state";
import { CHAIN_ID_MAPPING, getGaslessQuote, getQuote, weiToTokenAmount } from "@umami/swap";

import { type EffectiveOutput, type QuoteProvider, type QuoteWithProvider } from "./types";

export class ZeroXProvider implements QuoteProvider {
  canHandleSwap(
    sellToken: UnifiedBalanceWithPrices & MultichainTokenBalance,
    buyToken: UnifiedBalanceWithPrices & MultichainTokenBalance,
    getNetworkChain: (chainId: ChainFullId) => ChainId
  ): boolean {
    // ZeroX supports all same-chain swaps except Etherlink and Tezos
    return (
      getNetworkChain(sellToken.chainId) === getNetworkChain(buyToken.chainId) &&
      getNetworkChain(sellToken.chainId) !== ChainIds.ETHERLINK &&
      getNetworkChain(sellToken.chainId) !== ChainIds.TEZOS
    );
  }

  async fetchQuote(params: {
    sellToken: UnifiedBalanceWithPrices & MultichainTokenBalance;
    buyToken: UnifiedBalanceWithPrices & MultichainTokenBalance;
    sellAmount: string;
    userAddress: string;
    network: string;
    slippage: number;
    isGaslessSwap?: boolean;
    getNetworkChain: (chainId: ChainFullId) => ChainId;
  }): Promise<QuoteWithProvider | null> {
    const {
      sellToken,
      buyToken,
      sellAmount,
      userAddress,
      network,
      isGaslessSwap,
      getNetworkChain,
    } = params;

    try {
      if (isGaslessSwap && !this.isNativeTokenSwap(sellToken, buyToken)) {
        // Fetch gasless quote
        const sellTokenAddress = sellToken.address;
        const buyTokenAddress = buyToken.address;
        const sellAmountWei = (
          parseFloat(sellAmount) * Math.pow(10, sellToken.decimals || 18)
        ).toString();

        const gaslessQuote = await getGaslessQuote({
          sellToken: sellTokenAddress,
          buyToken: buyTokenAddress,
          sellAmount: sellAmountWei,
          takerAddress: userAddress,
          chainId: CHAIN_ID_MAPPING[getNetworkChain(sellToken.chainId)],
        });

        if (gaslessQuote) {
          const buyAmountInTokens = weiToTokenAmount(gaslessQuote.buyAmount, buyToken.decimals);

          return {
            provider: "Gasless",
            quote: gaslessQuote,
            buyAmount: buyAmountInTokens,
            sellAmount: sellAmount,
          };
        }
      } else {
        // Fetch regular ZeroX quote
        const zeroXQuote = await getQuote({
          sellToken: {
            ...sellToken,
            chainId: getNetworkChain(sellToken.chainId),
            address: sellToken.address,
          },
          buyToken: {
            ...buyToken,
            chainId: getNetworkChain(buyToken.chainId),
            address: buyToken.address,
          },
          sellAmount,
          takerAddress: userAddress,
          network,
          gasless: isGaslessSwap,
        });

        const buyAmountInTokens = weiToTokenAmount(zeroXQuote.buyAmount, buyToken.decimals);

        return {
          provider: "0x",
          quote: zeroXQuote,
          buyAmount: buyAmountInTokens,
          sellAmount: sellAmount || "",
        };
      }
    } catch (error) {
      console.error("Error fetching ZeroX quote:", error);
      throw new Error(
        `Failed to fetch ${isGaslessSwap ? "gasless" : "ZeroX"} quote: ${error instanceof Error ? error.message : "Unknown error"}`
      );
    }

    return null;
  }

  getValueAndUnit(
    quote: QuoteWithProvider,
    buyToken: UnifiedBalanceWithPrices & MultichainTokenBalance,
    getTokenUsdPrice: (symbol: string, amount: number) => number
  ): EffectiveOutput {
    const buyAmountNum = parseFloat(quote.buyAmount);
    const buyAmountUSD = getTokenUsdPrice(buyToken.symbol, buyAmountNum);
    let estimatedGasUSD = 0;

    const quoteData = quote.quote as any;

    if (quoteData.gas && quoteData.gasPrice) {
      const gasInWei = parseFloat(quoteData.gas);
      const gasPriceInWei = parseFloat(quoteData.gasPrice);
      const gasCostInWei = gasInWei * gasPriceInWei;
      const gasCostInEth = gasCostInWei / Math.pow(10, 18);
      estimatedGasUSD = getTokenUsdPrice("ETH", gasCostInEth);
    }

    if (buyAmountUSD > 0 && estimatedGasUSD > 0) {
      const netUSD = buyAmountUSD - estimatedGasUSD;

      return { value: netUSD, unit: "USD", hasUSD: true };
    }

    return { value: buyAmountNum, unit: buyToken.symbol, hasUSD: false };
  }

  private isNativeTokenSwap(
    sellToken: UnifiedBalanceWithPrices & MultichainTokenBalance,
    buyToken: UnifiedBalanceWithPrices & MultichainTokenBalance
  ): boolean {
    const sellTokenAddress = sellToken.address;
    const buyTokenAddress = buyToken.address;

    // Check if either token is ETH (native token)
    const ETH_ADDRESSES = [
      "0xeeeeeeeeeeeeeeeeeeeeeeeeeeeeeeeeeeeeeeee", // ZeroX ETH address
      "0x0000000000000000000000000000000000000000", // Native ETH address
    ];

    return (
      ETH_ADDRESSES.includes(sellTokenAddress.toLowerCase()) ||
      ETH_ADDRESSES.includes(buyTokenAddress.toLowerCase())
    );
  }
}
